// SyncStatus — header connectivity pill (Live / Synced / Offline / ...).
// 2026-05-29 EXTRACTED verbatim from src/App.jsx (C1 monolith split).
//
// 2026-08-31 (Phase 2 «Офлайн видим», П2) — REFACTORED into a thin subscriber.
// Used to run its OWN setInterval(5000) + fetch(/health) + window.__statusFailCount
// debounce (see git history for the pre-refactor body — characterization tests in
// SyncStatus.test.jsx pinned that behavior first). All of that moved to
// src/lib/connection-state.js (`status`, `probeConnection`, `startHealthWatch`,
// `recordStrike`) so App.jsx's pulse (visible/focus/60s) and the create-gate
// (Dashboard/CustomerPayments) drive/read the SAME verdict this pill renders,
// instead of SyncStatus owning an isolated timer nobody else could see or trigger.
// This component now ONLY subscribes and renders — it does not call
// startHealthWatch itself (App.jsx does, once, on mount); the subscription alone
// is enough to reflect whatever the watch/pulse/probes are doing.
//
// `status` / `pendingSaves` are read as live ESM bindings from connection-state.js
// — reads reflect the current value after a setter runs elsewhere. React only
// finds out via subscribeConnection: useState holds the cached snapshot PRIMITIVE
// (a string) purely so setState bails via Object.is when a tick changed nothing
// observable; the actual rendered values (`status`, `pendingSaves.size`) are read
// fresh from the live bindings in the render body, not parsed out of the snapshot.
//
// F1-v3 (2026-06-13): Added `compact` prop.
// compact=true: glyph-only circular pill (~28px wide, CONSTANT regardless of
// connection state or pending-count N). Eliminates the ~105px worst-case width
// swing of the full form (Offline + pending-count suffix) that broke prior budgets.
// compact=false (default): full pill with label + pending count — unchanged.
// Pass compact={compact} from BlitzHeader's desktop-actions in Tier B (1024–1559).
import { useState, useEffect } from 'react';
import { status, pendingSaves, subscribeConnection, getConnectionSnapshot } from '../lib/connection-state.js';

// compact=true: glyph-only circular pill — no text label, no pending count.
// Width is small and CONSTANT (~28px) regardless of connection state or pending count.
// Full behaviour (text + pending count) preserved when compact is false/undefined.
// Backward-compatible: compact defaults to false (additive prop, no existing callers break).
function SyncStatus({ compact = false }) {
  // Second element unused on purpose — this state exists only to give React a
  // reason to re-render (Object.is bail-out on the cached snapshot primitive);
  // the values actually rendered below come from the live bindings, read fresh.
  const [, setSnapshot] = useState(getConnectionSnapshot());
  useEffect(() => subscribeConnection(() => setSnapshot(getConnectionSnapshot())), []);
  const pendingCount = pendingSaves.size;
  const isOk = status === "live";
  const isPoll = status === "poll";
  const isBad = status === "offline";
  const cfg = {
    live: { bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.30)", color: "#10B981", text: "Live" },
    poll: { bg: "rgba(56,189,248,0.10)", border: "rgba(56,189,248,0.30)", color: "#38BDF8", text: "Synced" },
    offline: { bg: "rgba(239,68,68,0.10)", border: "rgba(239,68,68,0.30)", color: "#EF4444", text: "Offline" },
    checking: { bg: "rgba(148,163,184,0.1)", border: "rgba(148,163,184,0.3)", color: "#94A3B8", text: "..." },
  }[status] || { bg: "rgba(148,163,184,0.1)", border: "rgba(148,163,184,0.3)", color: "#94A3B8", text: "..." };
  // Full tooltip always carries complete status + pending info (accessible in all tiers)
  const fullTitle = isOk ? "WebSocket live — real-time sync"
    : isPoll ? "Connected via polling — data synced"
    : isBad ? `Server offline — data saved locally${pendingCount > 0 ? ` (${pendingCount} pending)` : ""}`
    : "Checking...";
  if (compact) {
    // Minimal fixed-width glyph-only pill — no label text, no pending count suffix.
    // Saves ~100-115px vs full SyncStatus ("! Offline (N⏳)" worst case), making
    // compact tier deterministically fit 1024px regardless of connection state.
    return (
      <span
        data-status={status}
        title={fullTitle}
        aria-label={fullTitle}
        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 28, height: 28, borderRadius: "50%", flexShrink: 0, cursor: "default",
          background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color,
          fontSize: 13, fontWeight: 900, lineHeight: 1 }}>
        {isOk ? "✓" : isPoll ? "✓" : isBad ? "!" : "•"}
      </span>
    );
  }
  return (
    <div data-status={status} title={fullTitle}
      style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "default",
        background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 0.3 }}>
      <span style={{ width: 18, height: 18, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: 11, fontWeight: 900, color: "#FFF", lineHeight: 1,
        background: isOk ? "linear-gradient(135deg, #059669, #10B981)" : isPoll ? "linear-gradient(135deg, #0284C7, #38BDF8)" : isBad ? "linear-gradient(135deg, #DC2626, #EF4444)" : "#94A3B8",
        boxShadow: isOk ? "0 0 8px rgba(16,185,129,0.5)" : isPoll ? "0 0 8px rgba(56,189,248,0.4)" : isBad ? "0 0 8px rgba(239,68,68,0.5)" : "none" }}>
        {isOk ? "✓" : isPoll ? "✓" : isBad ? "!" : "•"}
      </span>
      {cfg.text}{pendingCount > 0 && <span style={{ fontSize: 10, opacity: 0.7, marginLeft: 2 }}>({pendingCount}⏳)</span>}
    </div>
  );
}

export { SyncStatus };
export default SyncStatus;
